import React from 'react';
// Importações do Firestore
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
// Importações para chamar a Cloud Function
import { setUserRole } from '../../services/apiService';
// Importações de ícones
import { Briefcase, Settings, X as IconX, UserPlus, ShieldCheck, UserX, Users, Trash2, Loader, ClipboardList, ChevronUp, ChevronDown, MapPin, Mail, Search, Sparkles } from 'lucide-react';
import LogsTab from '../admin/LogsTab';
import UserProfileModal from './UserProfileModal';
import ConfirmDialog from '../common/ConfirmDialog';
import UserAvatar from '../common/UserAvatar';
import { startPoleLocationUpdate, getTaskStatus, cancelTask, getEmailSettings, saveEmailSettings, getAiSettings, saveAiSettings } from '../../services/apiService';

const AI_MODES = {
    FREELLM_WITH_FALLBACK: 'freellm_with_fallback',
    DEEPSEEK_ONLY: 'deepseek_only',
};

/**
 * @file Componente do modal do Painel de Administração.
 * Permite gerenciar usuários e configurações, agora usando Cloud Functions para definir permissões.
 */
const AdminPanelModal = ({ auth, db, storage, appId, onClose, setModalMessage, currentUser, initialSettings }) => {
    const [users, setUsers] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [userToDelete, setUserToDelete] = React.useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false);
    const [polePrice, setPolePrice] = React.useState(null);
    const [polePriceDisplay, setPolePriceDisplay] = React.useState('');
    const [darkMap, setDarkMap] = React.useState(false);
    const [showStatusBar, setShowStatusBar] = React.useState(false);
    const [lossDetectionLevel, setLossDetectionLevel] = React.useState(null);
    const [isUpdatingRole, setIsUpdatingRole] = React.useState(null);
    const [activeTab, setActiveTab] = React.useState('users');
    const [userSearch, setUserSearch] = React.useState('');
    const [selectedUser, setSelectedUser] = React.useState(null);
    const [isConfirmUpdateLocationsOpen, setIsConfirmUpdateLocationsOpen] = React.useState(false);
    const [isUpdatingLocations, setIsUpdatingLocations] = React.useState(false);
    const [updateProgress, setUpdateProgress] = React.useState(0);
    const [updateTotal, setUpdateTotal] = React.useState(0);
    const [taskId, setTaskId] = React.useState(null);
    const [pollingIntervalId, setPollingIntervalId] = React.useState(null);

    // Email Config State
    const [emailSettings, setEmailSettings] = React.useState({
        user: '', password: '', host: '', port: 993, smtpHost: '', smtpPort: 587, subjectPattern: '', enabled: false
    });
    const [isSavingEmail, setIsSavingEmail] = React.useState(false);
    const [aiSettings, setAiSettings] = React.useState({
        mode: AI_MODES.FREELLM_WITH_FALLBACK,
        freeLlmConfigured: false,
        deepseekConfigured: false,
        freeLlmTimeoutMs: 15000,
    });
    const [isLoadingAiSettings, setIsLoadingAiSettings] = React.useState(false);
    const [isSavingAiSettings, setIsSavingAiSettings] = React.useState(false);

    React.useEffect(() => {
        // Cleanup interval on component unmount
        return () => {
            if (pollingIntervalId) {
                clearInterval(pollingIntervalId);
            }
        };
    }, [pollingIntervalId]);

    React.useEffect(() => {
        if (polePrice !== null && polePrice !== undefined) {
            setPolePriceDisplay(Number(polePrice).toFixed(2).replace('.', ','));
        } else {
            setPolePriceDisplay('');
        }
    }, [polePrice]);

    React.useEffect(() => {
        if (initialSettings) {
            setPolePrice(initialSettings.polePrice != null ? initialSettings.polePrice : null);
            setDarkMap(initialSettings.darkMapEnabled || false);
            setShowStatusBar(initialSettings.showStatusBar || false);
            setLossDetectionLevel(initialSettings.lossDetectionLevel != null ? initialSettings.lossDetectionLevel : null);
        }
        
        const fetchUsers = async () => {
            setIsLoading(true);
            try {
                const usersCollection = collection(db, 'users');
                const usersSnapshot = await getDocs(usersCollection);
                const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUsers(usersList);
            } catch (error) {
                console.error("Erro ao buscar usuários:", error);
                setModalMessage("Não foi possível carregar a lista de usuários.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchUsers();
    }, [db, initialSettings, setModalMessage]);

    React.useEffect(() => {
        if (activeTab === 'settings') {
            getEmailSettings().then(res => {
                if (res.success && res.data) {
                     setEmailSettings(prev => ({ ...prev, ...res.data }));
                }
            }).catch(console.error);

            setIsLoadingAiSettings(true);
            getAiSettings().then(res => {
                if (res.success && res.data) {
                    setAiSettings(prev => ({ ...prev, ...res.data }));
                }
            }).catch(error => {
                console.error(error);
                setModalMessage("Não foi possível carregar a configuração de IA.");
            }).finally(() => setIsLoadingAiSettings(false));
        }
    }, [activeTab, setModalMessage]);

    const handleUpdateRole = async (userId, newRole) => {
        setIsUpdatingRole(userId);
        try {
            const result = await setUserRole(userId, newRole);

            if (result.success) {
                setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
                setModalMessage(result.message || "Privilégio do usuário atualizado com sucesso.");

                // ========================================================================
                // --- CORREÇÃO DEFINITIVA ---
                // Força a atualização do token do usuário atual para que as novas
                // permissões (Custom Claims) sejam aplicadas imediatamente.
                // ========================================================================
                if (auth.currentUser) {
                    await auth.currentUser.getIdToken(true);
                    console.log("Token do usuário atualizado com as novas permissões.");
                }

            } else {
                throw new Error(result.message || "A função de atualização de permissão retornou um erro.");
            }
        } catch (error) {
            console.error("Erro ao atualizar privilégio:", error);
            setModalMessage(error.message || "Falha ao atualizar privilégio.");
        } finally {
            setIsUpdatingRole(null);
        }
    };

    const handleOpenConfirmDialog = (user) => {
        setUserToDelete(user);
        setIsConfirmDialogOpen(true);
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        if (userToDelete.id === currentUser.uid) {
            setModalMessage("Você não pode remover sua própria conta de administrador.");
            return;
        }
        
        const userDocRef = doc(db, 'users', userToDelete.id);
        try {
            await deleteDoc(userDocRef);
            setUsers(users.filter(u => u.id !== userToDelete.id));
            setModalMessage("Usuário removido com sucesso.");
        } catch (error) {
            console.error("Erro ao remover usuário:", error);
            setModalMessage("Falha ao remover usuário.");
        } finally {
            setIsConfirmDialogOpen(false);
            setUserToDelete(null);
        }
    };

    const handlePriceInputChange = (e) => {
        let val = e.target.value.replace(/[^0-9,]/g, '').replace(/,(?=.*,)/g, '');
        setPolePriceDisplay(val);
    };

    const handlePriceInputBlur = () => {
        const num = parseFloat(polePriceDisplay.replace(',', '.'));
        setPolePrice(isNaN(num) ? null : num);
    };

    const handlePriceStep = (amount) => {
        const currentValue = polePrice || 0;
        const newValue = Math.max(0, currentValue + amount);
        setPolePrice(Number(newValue.toFixed(2)));
    };

    const handleSaveSettings = async () => {
        const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'config');
        try {
            await updateDoc(settingsRef, { 
                polePrice: polePrice,
                darkMapEnabled: darkMap,
                showStatusBar: showStatusBar,
                lossDetectionLevel: lossDetectionLevel,
            }, { merge: true });
            setModalMessage("Configurações salvas com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar configurações:", error);
            setModalMessage("Falha ao salvar configurações.");
        }
    };

    const handleEmailSettingChange = (field, value) => {
        setEmailSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveEmailSettings = async () => {
        setIsSavingEmail(true);
        try {
            await saveEmailSettings(emailSettings);
            setModalMessage("Configurações de e-mail salvas e monitor atualizado!");
        } catch (error) {
            setModalMessage("Erro ao salvar configurações de e-mail.");
        } finally {
            setIsSavingEmail(false);
        }
    };

    const handleToggleAiMode = async () => {
        if (isSavingAiSettings || isLoadingAiSettings) return;
        const previousMode = aiSettings.mode;
        const nextMode = previousMode === AI_MODES.DEEPSEEK_ONLY
            ? AI_MODES.FREELLM_WITH_FALLBACK
            : AI_MODES.DEEPSEEK_ONLY;

        setAiSettings(prev => ({ ...prev, mode: nextMode }));
        setIsSavingAiSettings(true);
        try {
            const result = await saveAiSettings(nextMode);
            if (!result.success) throw new Error(result.message);
            setModalMessage(nextMode === AI_MODES.DEEPSEEK_ONLY
                ? "IA configurada para usar somente o DeepSeek."
                : "IA configurada para usar FreeLLMAPI com fallback para DeepSeek.");
        } catch (error) {
            console.error("Erro ao salvar modo de IA:", error);
            setAiSettings(prev => ({ ...prev, mode: previousMode }));
            setModalMessage("Não foi possível alterar o modo de IA.");
        } finally {
            setIsSavingAiSettings(false);
        }
    };

    const handleOpenUserProfile = (user) => {
        setSelectedUser(user);
    };

    const pollTaskStatus = React.useCallback(async (currentTaskId) => {
        if (!currentTaskId) {
            console.warn("pollTaskStatus called with null or undefined taskId.");
            return;
        }
        try {
            const result = await getTaskStatus(currentTaskId);
            if (result.success) {
                const { status, processed, total } = result.data;
                setUpdateProgress(processed);
                setUpdateTotal(total);

                if (status === 'complete' || status === 'error') {
                    clearInterval(pollingIntervalId);
                    setPollingIntervalId(null);
                    setIsUpdatingLocations(false);
                    setTaskId(null);
                    setModalMessage(status === 'complete' ? `Endereços de ${processed} postes atualizados com sucesso!` : `Ocorreu um erro ao atualizar os endereços dos postes.`);
                }
            } else {
                throw new Error(result.message || 'Falha ao obter status da tarefa.');
            }
        } catch (error) {
            console.error("Erro ao sondar o status da tarefa:", error);
            clearInterval(pollingIntervalId);
            setPollingIntervalId(null);
            setIsUpdatingLocations(false);
            setModalMessage("Não foi possível acompanhar o progresso da atualização.");
        }
    }, [pollingIntervalId, setModalMessage]);


    const handleStartLocationUpdate = async () => {
        setIsConfirmUpdateLocationsOpen(false);
        setIsUpdatingLocations(true);
        setUpdateProgress(0);
        setUpdateTotal(0);

        try {
            const result = await startPoleLocationUpdate();
            if (result.success) {
                if (result.total > 0 && result.taskId) {
                    setTaskId(result.taskId);
                    setUpdateTotal(result.total);
                    setModalMessage(`Iniciando atualização para ${result.total} postes. Isso pode levar alguns minutos.`);

                    const intervalId = setInterval(() => {
                        pollTaskStatus(result.taskId);
                    }, 5000); // Poll every 5 seconds
                    setPollingIntervalId(intervalId);
                } else {
                    // No posts to update
                    setModalMessage(result.message || "Nenhum poste para atualizar.");
                    setIsUpdatingLocations(false);
                }
            } else {
                throw new Error(result.message || 'A função de início retornou um erro inesperado.');
            }
        } catch (error) {
            console.error("Erro ao iniciar a atualização da localização dos postes:", error);
            setModalMessage(error.message || "Não foi possível iniciar a atualização.");
            setIsUpdatingLocations(false);
        }
    };
    
    const handleCancelLocationUpdate = async () => {
        if (pollingIntervalId) {
            clearInterval(pollingIntervalId);
        }

        if (taskId) {
            try {
                await cancelTask(taskId);
                setModalMessage("Solicitação de cancelamento enviada. A tarefa será interrompida em breve.");
            } catch (error) {
                console.error("Erro ao cancelar a tarefa:", error);
                setModalMessage("Erro ao tentar cancelar a tarefa no servidor, mas o acompanhamento foi encerrado.");
            }
        } else {
            setModalMessage("Acompanhamento da atualização cancelado pelo usuário.");
        }

        setIsUpdatingLocations(false);
        setPollingIntervalId(null);
        setTaskId(null);
    };

    const TabButton = ({ id, label, icon: Icon, active, onClick }) => {
        const baseClasses = "flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors";
        const activeClasses = "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white";
        const inactiveClasses = "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800";
        return (
            <button onClick={() => onClick(id)} className={`${baseClasses} ${active ? activeClasses : inactiveClasses}`}>
                <Icon className="mr-2 h-5 w-5" />
                {label}
            </button>
        );
    };

    const renderContent = () => {
        if (isLoading && activeTab !== 'logs') { // A aba de logs tem seu próprio loader
            return <div className="flex justify-center items-center h-64"><Loader className="animate-spin h-16 w-16 text-blue-500"/></div>;
        }

        switch (activeTab) {
            case 'users':
                const normalizedSearch = userSearch
                    .trim()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase();
                const matchesSearch = (user) => !normalizedSearch || [user.displayName, user.email]
                    .filter(Boolean)
                    .some(value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(normalizedSearch));
                const pendingUsers = users.filter(u => u.role === 'pending' && u.email && matchesSearch(u));
                const approvedUsers = users.filter(u => u.role !== 'pending' && u.email && matchesSearch(u));
                return (
                    <div className="space-y-8">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                            <input
                                type="search"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                placeholder="Buscar por nome ou e-mail"
                                aria-label="Buscar usuários por nome ou e-mail"
                                className="input-style w-full pl-10"
                            />
                        </div>
                        {pendingUsers.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-4 flex items-center"><UserPlus className="mr-2 text-amber-500"/>Usuários Aguardando Aprovação</h3>
                                <div className="space-y-3">
                                    {pendingUsers.map(user => (
                                        <div key={user.id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3">
                                            <div>
                                                <p className="font-semibold text-slate-800 dark:text-slate-100">{user.displayName || 'Usuário sem nome'}</p>
                                                <p className="text-sm text-slate-500 dark:text-slate-400">{user.email}</p>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <button onClick={() => handleUpdateRole(user.id, 'tecnico')} className="btn-admin bg-blue-500 hover:bg-blue-600" disabled={isUpdatingRole === user.id}>
                                                    {isUpdatingRole === user.id ? <Loader className="animate-spin h-4 w-4" /> : <><ShieldCheck className="mr-2 h-4 w-4"/>Aprovar como Técnico</>}
                                                </button>
                                                <button onClick={() => handleUpdateRole(user.id, 'tecnico_campo')} className="btn-admin bg-indigo-500 hover:bg-indigo-600" disabled={isUpdatingRole === user.id}>
                                                    {isUpdatingRole === user.id ? <Loader className="animate-spin h-4 w-4" /> : <><ShieldCheck className="mr-2 h-4 w-4"/>Aprovar como Téc. Campo</>}
                                                </button>
                                                <button onClick={() => handleUpdateRole(user.id, 'atendimento')} className="btn-admin bg-orange-500 hover:bg-orange-600" disabled={isUpdatingRole === user.id}>
                                                    {isUpdatingRole === user.id ? <Loader className="animate-spin h-4 w-4" /> : <><ShieldCheck className="mr-2 h-4 w-4"/>Aprovar como Atendimento</>}
                                                </button>
                                                <button onClick={() => handleUpdateRole(user.id, 'comercial')} className="btn-admin bg-emerald-500 hover:bg-emerald-600" disabled={isUpdatingRole === user.id}>
                                                    {isUpdatingRole === user.id ? <Loader className="animate-spin h-4 w-4" /> : <><ShieldCheck className="mr-2 h-4 w-4"/>Aprovar como Comercial</>}
                                                </button>
                                                <button onClick={() => handleUpdateRole(user.id, 'administrador')} className="btn-admin bg-green-500 hover:bg-green-600" disabled={isUpdatingRole === user.id}>
                                                    {isUpdatingRole === user.id ? <Loader className="animate-spin h-4 w-4" /> : <><ShieldCheck className="mr-2 h-4 w-4"/>Aprovar como Admin</>}
                                                </button>
                                                <button onClick={() => handleOpenConfirmDialog(user)} className="btn-admin bg-red-500 hover:bg-red-600" disabled={isUpdatingRole === user.id}><UserX className="mr-2 h-4 w-4"/>Rejeitar</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div>
                            <h3 className="text-lg font-semibold mb-4 flex items-center"><Users className="mr-2 text-blue-500"/>Gerenciar Usuários Ativos</h3>
                            <div className="space-y-3">
                                {approvedUsers.map(user => (
                                    <div
                                        key={user.id}
                                        onClick={() => handleOpenUserProfile(user)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleOpenUserProfile(user);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Gerenciar usuário ${user.displayName || 'sem nome'}`}
                                        className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                                    >
                                        <div className="flex items-center gap-4">
                                            <UserAvatar user={user} size="w-10 h-10" />
                                            <div>
                                                <p className="font-semibold text-slate-800 dark:text-slate-100">{user.displayName || 'Usuário sem nome'}</p>
                                                <p className="text-sm text-slate-500 dark:text-slate-400">{user.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <select value={user.role} onClick={(e) => e.stopPropagation()} onChange={(e) => {e.stopPropagation(); handleUpdateRole(user.id, e.target.value)}} className="input-style px-4" disabled={user.id === currentUser.uid || isUpdatingRole === user.id}>
                                                <option value="tecnico">Técnico de Rede</option>
                                                <option value="tecnico_campo">Técnico de Campo</option>
                                                <option value="atendimento">Atendimento</option>
                                                <option value="comercial">Comercial</option>
                                                <option value="administrador">Administrador</option>
                                            </select>
                                            <button onClick={(e) => {e.stopPropagation(); handleOpenConfirmDialog(user)}} aria-label={`Excluir usuário ${user.displayName || 'sem nome'}`} title="Excluir usuário" className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full disabled:text-slate-400 disabled:hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-red-500" disabled={user.id === currentUser.uid || isUpdatingRole === user.id}>
                                                <Trash2 className="h-5 w-5"/>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {userSearch.trim() && pendingUsers.length === 0 && approvedUsers.length === 0 && (
                                    <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">Nenhum usuário encontrado.</p>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 'settings':
                return (
                    <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center"><Settings className="mr-2 text-slate-500"/>Configurações Gerais</h3>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-4">
                           <div>
                                <label htmlFor="polePrice" className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Valor Mensal por Poste</label>
                                <div className="flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-shadow duration-200">
                                    <span className="flex items-center justify-center px-3 h-10 bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border-r border-slate-300 dark:border-slate-600 rounded-l-md">
                                        R$
                                    </span>
                                    <input
                                        type="text"
                                        id="polePrice"
                                        value={polePriceDisplay}
                                        onChange={handlePriceInputChange}
                                        onBlur={handlePriceInputBlur}
                                        className="input-style px-4 w-full border-none shadow-none focus:ring-0 bg-transparent"
                                        placeholder="Ex: 15,75"
                                    />
                                    <div className="flex flex-col border-l border-slate-300 dark:border-slate-600">
                                        <button onClick={() => handlePriceStep(0.01)} className="flex-1 px-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-tr-md flex items-center justify-center" aria-label="Aumentar valor">
                                            <ChevronUp className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => handlePriceStep(-0.01)} className="flex-1 px-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border-t border-slate-300 dark:border-slate-600 rounded-br-md flex items-center justify-center" aria-label="Diminuir valor">
                                            <ChevronDown className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                           <div className="flex items-center justify-between">
                                <label htmlFor="darkMapToggle" className="block text-sm font-medium text-slate-600 dark:text-slate-400">Habilitar Mapa Escuro</label>
                                <label htmlFor="darkMapToggle" className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" id="darkMapToggle" className="sr-only peer" checked={darkMap} onChange={() => setDarkMap(!darkMap)} />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                                </label>
                           </div>

                           <div className="flex items-center justify-between">
                                <label htmlFor="statusBarToggle" className="block text-sm font-medium text-slate-600 dark:text-slate-400">Exibir Barra de Status do Sistema</label>
                                <label htmlFor="statusBarToggle" className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" id="statusBarToggle" className="sr-only peer" checked={showStatusBar} onChange={() => setShowStatusBar(!showStatusBar)} />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                                </label>
                           </div>

                           <div>
                                <label htmlFor="lossDetectionLevel" className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Nível de detecção de loss (%)</label>
                                <input
                                    type="number"
                                    id="lossDetectionLevel"
                                    value={lossDetectionLevel || ''}
                                    onChange={(e) => setLossDetectionLevel(parseInt(e.target.value, 10) || null)}
                                    className="input-style px-4 w-full"
                                    placeholder="Ex: 10"
                                />
                           </div>

                           <div className="flex justify-end">
                             <button onClick={handleSaveSettings} className="btn-primary">Salvar Configurações</button>
                           </div>
                        </div>

                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-4 mt-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="text-lg font-semibold flex items-center"><Sparkles className="mr-2 text-violet-500"/>Provedor de Inteligência Artificial</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                        {aiSettings.mode === AI_MODES.DEEPSEEK_ONLY
                                            ? 'Todas as solicitações são enviadas diretamente ao DeepSeek.'
                                            : `O FreeLLMAPI é prioritário. Se não responder em até ${(aiSettings.freeLlmTimeoutMs / 1000).toLocaleString('pt-BR')} segundos ou estiver indisponível, o DeepSeek assume automaticamente.`}
                                    </p>
                                </div>
                                {(isLoadingAiSettings || isSavingAiSettings) && <Loader className="animate-spin h-5 w-5 text-violet-500 shrink-0" />}
                            </div>

                            <div className="flex items-center justify-between gap-4 p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                                <div>
                                    <p className="font-medium text-slate-700 dark:text-slate-200">
                                        {aiSettings.mode === AI_MODES.DEEPSEEK_ONLY ? 'Somente DeepSeek' : 'FreeLLMAPI + fallback'}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        FreeLLMAPI: {aiSettings.freeLlmConfigured ? 'configurado' : 'não configurado'} · DeepSeek: {aiSettings.deepseekConfigured ? 'configurado' : 'não configurado'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleToggleAiMode}
                                    disabled={isLoadingAiSettings || isSavingAiSettings}
                                    className="btn-secondary whitespace-nowrap disabled:opacity-50"
                                >
                                    Alternar para {aiSettings.mode === AI_MODES.DEEPSEEK_ONLY ? 'FreeLLM + fallback' : 'somente DeepSeek'}
                                </button>
                            </div>

                            {!aiSettings.freeLlmConfigured && aiSettings.mode === AI_MODES.FREELLM_WITH_FALLBACK && (
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                    FREELLM_API_KEY não está configurada; enquanto isso, as solicitações irão direto para o fallback DeepSeek.
                                </p>
                            )}
                        </div>

                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-4 mt-4">
                            <h3 className="text-lg font-semibold mb-4 flex items-center"><Mail className="mr-2 text-slate-500"/>Configuração de Monitoramento de Notificações</h3>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label htmlFor="emailMonitorToggle" className="block text-sm font-medium text-slate-600 dark:text-slate-400">Monitorar notificações</label>
                                    <label htmlFor="emailMonitorToggle" className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" id="emailMonitorToggle" className="sr-only peer" checked={emailSettings.enabled} onChange={(e) => handleEmailSettingChange('enabled', e.target.checked)} />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>

                                {emailSettings.enabled && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Padrão de assunto (Filtro)</label>
                                            <input type="text" value={emailSettings.subjectPattern} onChange={(e) => handleEmailSettingChange('subjectPattern', e.target.value)} className="input-style px-4 w-full" placeholder="Ex: Notificação de Irregularidade" />
                                            <p className="text-xs text-slate-500 mt-1">O sistema buscará e-mails novos que contenham este texto no assunto.</p>
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">E-mail</label>
                                            <input type="email" value={emailSettings.user} onChange={(e) => handleEmailSettingChange('user', e.target.value)} className="input-style px-4 w-full" placeholder="seu-email@exemplo.com" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Senha</label>
                                            <input type="password" value={emailSettings.password} onChange={(e) => handleEmailSettingChange('password', e.target.value)} className="input-style px-4 w-full" placeholder="********" />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Servidor IMAP</label>
                                            <input type="text" value={emailSettings.host} onChange={(e) => handleEmailSettingChange('host', e.target.value)} className="input-style px-4 w-full" placeholder="imap.exemplo.com" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Porta IMAP</label>
                                            <input type="number" value={emailSettings.port} onChange={(e) => handleEmailSettingChange('port', parseInt(e.target.value))} className="input-style px-4 w-full" placeholder="993" />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Servidor SMTP</label>
                                            <input type="text" value={emailSettings.smtpHost} onChange={(e) => handleEmailSettingChange('smtpHost', e.target.value)} className="input-style px-4 w-full" placeholder="smtp.exemplo.com" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Porta SMTP</label>
                                            <input type="number" value={emailSettings.smtpPort} onChange={(e) => handleEmailSettingChange('smtpPort', parseInt(e.target.value))} className="input-style px-4 w-full" placeholder="587" />
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end pt-2">
                                    <button onClick={handleSaveEmailSettings} disabled={isSavingEmail} className="btn-primary flex items-center">
                                        {isSavingEmail && <Loader className="animate-spin h-4 w-4 mr-2" />}
                                        Salvar Configuração de E-mail
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-4 mt-4">
                            <div>
                                <h4 className="text-md font-semibold mb-2 flex items-center"><MapPin className="mr-2 text-slate-500"/>Manutenção de Dados</h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                    Execute tarefas de manutenção para manter os dados do sistema atualizados.
                                    Estes processos podem ser demorados e consumir recursos.
                                </p>

                                {!isUpdatingLocations ? (
                                    <button onClick={() => setIsConfirmUpdateLocationsOpen(true)} className="btn-secondary w-full flex items-center justify-center">
                                        <MapPin className="mr-2 h-4 w-4"/>
                                        Atualizar Localização de Postes
                                    </button>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Atualizando endereços dos postes...</span>
                                            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{updateProgress} / {updateTotal}</span>
                                        </div>
                                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${(updateProgress / (updateTotal || 1)) * 100}%` }}></div>
                                        </div>
                                        <button onClick={handleCancelLocationUpdate} className="btn-admin bg-red-500 hover:bg-red-600 justify-center w-full mt-2 text-sm">
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 'logs':
                return <LogsTab users={users} />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 z-[1050] flex justify-center items-center p-4" onClick={onClose}>
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                        <h2 className="text-xl font-bold flex items-center"><Settings className="mr-3"/>Painel de Administração</h2>
                        <button onClick={onClose} aria-label="Fechar" title="Fechar" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"><IconX /></button>
                    </header>

                    <div className="p-2 border-b border-slate-200 dark:border-slate-700">
                        <nav className="flex space-x-1">
                            <TabButton id="users" label="Usuários" icon={Users} active={activeTab === 'users'} onClick={setActiveTab} />
                            <TabButton id="settings" label="Configurações" icon={Settings} active={activeTab === 'settings'} onClick={setActiveTab} />
                            <TabButton id="logs" label="Registros" icon={ClipboardList} active={activeTab === 'logs'} onClick={setActiveTab} />
                        </nav>
                    </div>

                    <div className="flex-grow overflow-y-auto p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>
            {selectedUser && (
                <UserProfileModal
                    currentUser={currentUser}
                    targetUser={selectedUser}
                    auth={auth}
                    db={db}
                    storage={storage}
                    onClose={() => setSelectedUser(null)}
                    setModalMessage={setModalMessage}
                />
            )}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleDeleteUser}
                title="Confirmar Exclusão"
                message={`Tem certeza de que deseja excluir o usuário ${userToDelete?.displayName || 'este usuário'}? Esta ação não pode ser desfeita.`}
                isProcessing={isLoading}
            />
            <ConfirmDialog
                isOpen={isConfirmUpdateLocationsOpen}
                onClose={() => setIsConfirmUpdateLocationsOpen(false)}
                onConfirm={handleStartLocationUpdate}
                title="Atualizar Localização de Postes"
                message="Este processo irá buscar o endereço para todos os postes sem localização definida. A operação pode ser demorada e pode gerar custos de API dependendo do número de postes. Deseja continuar?"
                isProcessing={isUpdatingLocations}
            />
        </>
    );
};

export default AdminPanelModal;
